import type { MouseEvent, ReactNode } from 'react';

import type { ApiUserEnvironmentOrgMini } from '#/api/userEnvironment/types/ApiUserEnvironmentOrgMini';
import { ApiAndIntegrationsDropdown } from '#/components/ApiAndIntegrationsDropdown/ApiAndIntegrationsDropdown';
import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { DropdownHeaderTrigger } from '#/components/Dropdown/HeaderTrigger/DropdownHeaderTrigger';
import { FindAJobDropdown } from '#/components/FindAJobDropdown/FindAJobDropdown';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { ParticipateDropdown } from '#/components/ParticipateDropdown/ParticipateDropdown';
import { PostOnIdealistDropdown } from '#/components/PostOnIdealistDropdown/PostOnIdealistDropdown';
import { VolunteerDropdown } from '#/components/VolunteerDropdown/VolunteerDropdown';
import { useIsClient } from '#/hooks/useIsClient';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { homeRoute } from '#/routing/routes';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import {
  PageHeaderApiAndIntegrationsDropdownHolder,
  PageHeaderAuthHolder,
  PageHeaderAuthLink,
  PageHeaderButtonOrLink,
  PageHeaderCartDropdownButtonHolder,
  PageHeaderDesktopDonateLinkHolder,
  PageHeaderDesktopSearchHolder,
  PageHeaderHolder,
  PageHeaderLeft,
  PageHeaderLinksHolder,
  PageHeaderLogoLink,
  PageHeaderMobileLinksPlaceholder,
  PageHeaderMobileSearchHolder,
  PageHeaderMyAccountHolder,
  PageHeaderNotificationsDropdownHolder,
  PageHeaderRight,
  PageHeaderSignupLinkHolder,
  PageHeaderTopSectionHolder,
} from './PageHeader.styled';
import { VolunteerLink } from './VolunteerLink';

type Props = {
  userDisplayName: string | undefined;
  isUserLoaded: boolean;
  orgs: ApiUserEnvironmentOrgMini[];

  loginHref: string;
  onClickLogin: (event: MouseEvent<HTMLAnchorElement>) => void;

  signupHref: string;
  onClickSignup: (event: MouseEvent<HTMLAnchorElement>) => void;

  donate: {
    text: string;
    href: string;
  };

  myAccountDropdown: ReactNode;
  notificationsDropdown: ReactNode;
  cartDropdownButton: ReactNode;

  desktopSearchBar: ReactNode;
  mobileSearchDropdown: ReactNode;

  /*
   * HTML that needs to be sticky alongside the header
   */
  below: ReactNode;
};

const IDEALIST_DAYS_PATH = {
  en: '/en/days/get-involved',
  es: '/es/dias/participa',
  pt: '/pt/days/participe',
};

export function PageHeader({
  userDisplayName,
  isUserLoaded,
  orgs,
  loginHref,
  onClickLogin,
  signupHref,
  onClickSignup,
  donate,
  notificationsDropdown,
  myAccountDropdown,
  cartDropdownButton,
  desktopSearchBar,
  mobileSearchDropdown,
  below,
}: Props) {
  const { i18n, locale } = useI18n();
  const isLoggedIn = typeof userDisplayName !== 'undefined';

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  return (
    <PageHeaderHolder data-qa-id="page-header">
      <PageHeaderTopSectionHolder>
        <PageHeaderLeft>
          <PageHeaderLogoLink
            to={homeRoute.with({ lang: locale })}
            title="Idealist"
            onClick={() => trackClicked('Idealist Logo Header')}
            qaId="header-logo-link"
          >
            <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
          </PageHeaderLogoLink>

          <PageHeaderLinksHolder>
            {locale === I18nLocale.ES || locale === I18nLocale.PT ? (
              <ParticipateDropdown
                renderTrigger={({ triggerText, triggerHref }) => (
                  <DropdownHeaderTrigger
                    variant="grey"
                    qaId="page-header-participate-dropdown-trigger"
                    asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                    onClick={() => {
                      if (isDesktop) {
                        trackClicked('Participate', { context: 'Header' });
                      }
                    }}
                  >
                    {triggerText} <DropdownChevron />
                  </DropdownHeaderTrigger>
                )}
                trackClickedContext="Header Dropdown > Participate"
              />
            ) : (
              <PageHeaderButtonOrLink
                href={IDEALIST_DAYS_PATH[locale]}
                onClick={() =>
                  trackClicked('Idealist Days', { context: 'Header' })
                }
                type="link"
              >
                Idealist Days
              </PageHeaderButtonOrLink>
            )}

            <FindAJobDropdown
              renderTrigger={({ triggerText, triggerHref }) => (
                <DropdownHeaderTrigger
                  variant="grey"
                  qaId="page-header-find-a-job-dropdown-trigger"
                  asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                  onClick={() => {
                    if (isDesktop) {
                      trackClicked('Find a Job', { context: 'Header' });
                    }
                  }}
                >
                  {triggerText} <DropdownChevron />
                </DropdownHeaderTrigger>
              )}
              trackClickedContext="Header Dropdown > Find a Job"
            />

            {[I18nLocale.EN, I18nLocale.ES].includes(locale) ? (
              <VolunteerDropdown
                renderTrigger={({ triggerText, triggerHref }) => (
                  <DropdownHeaderTrigger
                    variant="grey"
                    qaId="page-header-volunteer-dropdown-trigger"
                    asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                    onClick={() => {
                      if (isDesktop) {
                        trackClicked('Volunteer', { context: 'Header' });
                      }
                    }}
                  >
                    {triggerText} <DropdownChevron />
                  </DropdownHeaderTrigger>
                )}
                trackClickedContext="Header Dropdown > Volunteer"
              />
            ) : (
              <VolunteerLink />
            )}

            <PostOnIdealistDropdown
              renderTrigger={({ triggerText, triggerHref }) => (
                <DropdownHeaderTrigger
                  variant="grey"
                  qaId="page-header-post-on-idealist-dropdown-trigger"
                  asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                  onClick={() => {
                    if (isDesktop) {
                      trackClicked('Post a Listing', { context: 'Header' });
                    }
                  }}
                >
                  {triggerText} <DropdownChevron />
                </DropdownHeaderTrigger>
              )}
              trackClickedContext="Header Dropdown > Post on Idealist"
            />

            {locale === I18nLocale.EN && (
              <PageHeaderApiAndIntegrationsDropdownHolder>
                <ApiAndIntegrationsDropdown
                  renderTrigger={({ triggerText, triggerHref }) => (
                    <DropdownHeaderTrigger
                      variant="grey"
                      qaId="page-header-api-and-integrations-dropdown-trigger"
                      asRouterLink={isDesktop ? { to: triggerHref } : undefined}
                      onClick={() => {
                        if (isDesktop) {
                          trackClicked('API & Integrations', {
                            context: 'Header',
                          });
                        }
                      }}
                    >
                      {triggerText} <DropdownChevron />
                    </DropdownHeaderTrigger>
                  )}
                  trackClickedContext="Header Dropdown > API & Integrations"
                />
              </PageHeaderApiAndIntegrationsDropdownHolder>
            )}

            {orgs.length === 0 && (
              <PageHeaderDesktopDonateLinkHolder>
                <PageHeaderButtonOrLink
                  href={donate.href}
                  onClick={() => trackClicked('Donate', { context: 'Header' })}
                  type="link"
                >
                  {donate.text}
                </PageHeaderButtonOrLink>
              </PageHeaderDesktopDonateLinkHolder>
            )}
          </PageHeaderLinksHolder>
        </PageHeaderLeft>

        <PageHeaderRight>
          {isUserLoaded && !isLoggedIn && (
            <PageHeaderAuthHolder>
              <PageHeaderAuthLink
                href={loginHref}
                rel="nofollow"
                onClick={(e) => {
                  trackClicked('Log In Header');
                  onClickLogin(e);
                }}
                data-qa-id="login-link"
                aria-label={i18n('Log In')}
              >
                {i18n('Log In')}
              </PageHeaderAuthLink>

              <PageHeaderSignupLinkHolder>
                <PageHeaderAuthLink
                  href={signupHref}
                  rel="nofollow"
                  onClick={(e) => {
                    trackClicked('Signup Header');
                    onClickSignup(e);
                  }}
                  data-qa-id="signup-link"
                  aria-label={i18n('Sign Up')}
                >
                  {i18n('Sign Up')}
                </PageHeaderAuthLink>
              </PageHeaderSignupLinkHolder>
            </PageHeaderAuthHolder>
          )}

          <PageHeaderMobileSearchHolder>
            {mobileSearchDropdown}
          </PageHeaderMobileSearchHolder>

          {isLoggedIn && (
            <PageHeaderMyAccountHolder>
              {myAccountDropdown}
            </PageHeaderMyAccountHolder>
          )}

          {isLoggedIn && (
            <PageHeaderNotificationsDropdownHolder>
              {notificationsDropdown}
            </PageHeaderNotificationsDropdownHolder>
          )}

          {orgs.length > 0 && (
            <PageHeaderCartDropdownButtonHolder>
              {cartDropdownButton}
            </PageHeaderCartDropdownButtonHolder>
          )}
        </PageHeaderRight>
      </PageHeaderTopSectionHolder>

      <PageHeaderMobileLinksPlaceholder />

      {desktopSearchBar && (
        <PageHeaderDesktopSearchHolder data-qa-id="page-header-search-menu">
          {desktopSearchBar}
        </PageHeaderDesktopSearchHolder>
      )}

      {below}
    </PageHeaderHolder>
  );
}
